import { resolveFormValidationError } from "@/utils/formUtils";
import { validations } from "@/utils/validationUtils";
import { App as AntApp, Checkbox, Col, Form, Modal, Row, Upload } from "antd";
import { RcFile } from "antd/lib/upload";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { HttpContentType } from "../../../../../common/api/http";
import { useImportClients } from "../../../../../common/api/queries/client";
import ActionButton from "../../../../../common/components/buttons/ActionButton";
import LabelWithTooltip from "../../../../../common/components/form/labels/LabelWithTooltip";
import { FileUploadAccept, ModalSizes, rowGutter } from "../../../../../common/constants";

interface Props {
  open: boolean;
  onFormCancel: () => void;
}

const ClientsImportForm = ({ open, onFormCancel }: Props) => {
  const { t } = useTranslation();
  const { notification } = AntApp.useApp();

  const [form] = Form.useForm<{ dryRun: boolean; allowUpdate: boolean }>();

  const importClients = useImportClients();

  const [file, setFile] = useState<RcFile>();

  const handleFileUpload = (file: RcFile) => {
    if (!HttpContentType.CSV.includes(file.type)) {
      notification.error({ title: t("common.error"), description: t("helpers.onlyCsvSupported") });
      return Upload.LIST_IGNORE;
    }

    setFile(file);
    return false;
  };

  const handleFileRemove = () => {
    setFile(undefined);
    return true;
  };

  const handleAfterClosed = () => {
    setFile(undefined);
    form.resetFields();
  };

  const handleFormSubmit = () => {
    form
      .validateFields()
      .then((values) => {
        if (!file) {
          notification.error({ title: t("common.error"), description: t("helpers.missingInputFile") });
        } else {
          const formData = new FormData();
          formData.append("file", file);
          formData.append("dryRun", values.dryRun.toString());
          formData.append("allowUpdate", values.allowUpdate.toString());
          importClients.mutate(formData, { onSuccess: onFormCancel });
        }
      })
      .catch(resolveFormValidationError);
  };

  return (
    <Modal
      width={ModalSizes.TINY}
      open={open}
      title={t("client.titles.import")}
      okText={t("common.submit")}
      cancelText={t("common.cancel")}
      confirmLoading={importClients.isPending}
      onOk={handleFormSubmit}
      onCancel={onFormCancel}
      afterClose={handleAfterClosed}
      mask={{ closable: false }}
    >
      <Form form={form} layout="vertical" name="clientsImportForm">
        <Row gutter={rowGutter}>
          <Col span={24}>
            <Form.Item name="dryRun" valuePropName="checked" rules={[validations.none]} initialValue={true}>
              <Checkbox>
                <LabelWithTooltip
                  label={t("client.helpers.import.dryRun")}
                  tooltip={t("client.helpers.import.dryRunDesc")}
                />
              </Checkbox>
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={rowGutter}>
          <Col span={24}>
            <Form.Item name="allowUpdate" valuePropName="checked" rules={[validations.none]} initialValue={false}>
              <Checkbox>
                <LabelWithTooltip
                  label={t("client.helpers.import.allowUpdate")}
                  tooltip={t("client.helpers.import.allowUpdateDesc")}
                />
              </Checkbox>
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={rowGutter}>
          <Col span={24}>
            <Upload
              maxCount={1}
              accept={FileUploadAccept.CSV}
              fileList={file ? [file] : []}
              beforeUpload={handleFileUpload}
              onRemove={handleFileRemove}
            >
              <ActionButton icon="plus">{t("common.chooseFile")}</ActionButton>
            </Upload>
          </Col>
        </Row>
      </Form>
    </Modal>
  );
};

export default ClientsImportForm;
